import { useTranslation } from 'react-i18next';
import ModalFrame from '../primitives/ModalFrame';
import type { RealEstateCatalogItem } from './realEstateCatalog';


interface RealEstateQuotaModalProps {
  /** Будинок, на якому впала квота (може бути відсутнім у каталозі). */
  houseItem: RealEstateCatalogItem | undefined;
  onClose: () => void;
  onLearnMore: () => void;
}

/** Попап з помилкою досягнення квоти */
export default function RealEstateQuotaModal({
  houseItem,
  onClose,
  onLearnMore,
}: RealEstateQuotaModalProps) {
  const { t } = useTranslation();

  return (
    <ModalFrame
      isOpen={true}
      onClose={onClose}
      backdropClassName="modal-frame-align-start modal-frame-bottom-gradient [--modal-frame-gradient-max-width:520px]"
      showBackButton={false}
      renderHeader={() => (
        <header className="sticky top-0 z-[2010] mb-xl flex items-center justify-between bg-[image:var(--gradient-modal-header)] pt-2xl pb-xl">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="m-0 h-[36px] w-[36px] shrink-0 cursor-pointer border-none bg-transparent p-0 text-center text-[24px] leading-[36px] text-white"
            aria-label={t("Close")}
          >
            ×
          </button>
          <div className="flex flex-1 items-center justify-center text-center">
            <p className="m-0 max-w-[280px] text-[14px] font-normal text-white">
              {t("Failure :(")}
            </p>
          </div>
          <div className="h-[36px] w-[36px] shrink-0" />
        </header>
      )}
    >
      <div className="flex flex-col gap-xl pb-md">
        <p className="m-0 text-center text-[16px] font-bold">
          {t("You have reached the real estate limit")}
        </p>
        {/* Зображення будинку */}
        {houseItem ? (
          <div className="mb-md flex justify-center">
            <img
              src={houseItem.image}
              alt={houseItem.name}
              className="h-[126px] w-[120px] rounded-[18px] object-cover"
            />
          </div>
        ) : null}

        {/* Текст */}
        <div className="flex flex-col gap-lg text-center">
          <p className="m-0 text-[14px] leading-[1.5] opacity-85">
            {t("The island map has a limited number of building spots. To ensure fair distribution, each player can purchase up to 2 houses with pearls.")}
          </p>
          <svg
            width="24"
            height="32"
            viewBox="0 0 24 32"
            fill="none"
            className="self-center [filter:drop-shadow(0_0_10px_var(--color-accent-alpha-50))_drop-shadow(0_0_3px_var(--color-reward-alpha-40))]"
          >
            <defs>
              <linearGradient id="arrow-grad" x1="12" y1="2" x2="12" y2="30" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={'var(--color-gold)'}/>
                <stop offset="100%" stopColor={'var(--color-accent)'}/>
              </linearGradient>
            </defs>
            <path d="M12 2v22" stroke="url(#arrow-grad)" strokeWidth="2" strokeLinecap="round"/>
            <path d="M12 28l-5-6h10l-5 6z" fill="url(#arrow-grad)"/>
          </svg>
        </div>

        {/* VIP блок */}
        <div className="rounded-md border border-accent-alpha-30 bg-accent-alpha-15 p-xl">
          {/* Текст про VIP над зображенням */}
          <p className="mb-[10px] text-center text-[14px] font-bold text-accent">
            👑 {t("But VIP members get unlimited real estate!")}
          </p>

          {/* Зображення VIP */}
          <div className="mt-sm mb-lg flex justify-center">
            <img
              src="/ui/item-icons/1.png"
              alt="VIP"
              className="h-[84px] w-[84px] rounded-md object-cover"
            />
          </div>

          {/* Кнопка під заголовком */}
          <button
            onClick={onLearnMore}
            className="mb-[10px] w-full cursor-pointer rounded-lg border-none bg-[image:var(--gradient-gold-warm)] px-3xl py-[14px] text-[15px] font-bold text-black"
          >
            {t("Learn more")}
          </button>

          <div className="flex flex-col gap-md">
            <div className="flex items-center gap-md rounded-[10px] bg-white-alpha-8 px-[10px] py-md">
              <span className="w-5 shrink-0 text-center text-[15px] leading-none">⚡</span>
              <span className="text-[13px] leading-[1.35]">{t("+30% Energy")}</span>
            </div>
            <div className="flex items-center gap-md rounded-[10px] bg-white-alpha-8 px-[10px] py-md">
              <span className="inline-flex w-5 shrink-0 justify-center text-center">
                <img src="/ui/shell-icon.png" alt="Shell" className="h-[14px] w-[14px] object-contain" />
              </span>
              <span className="text-[13px] leading-[1.35]">{t("+20% Income")}</span>
            </div>
            <div className="flex items-center gap-md rounded-[10px] bg-white-alpha-8 px-[10px] py-md">
              <span className="w-5 shrink-0 text-center text-[15px] leading-none">🛡️</span>
              <span className="text-[13px] leading-[1.35]">{t("Protection from loss")}</span>
            </div>
            <div className="flex items-center gap-md rounded-[10px] bg-white-alpha-8 px-[10px] py-md">
              <span className="w-5 shrink-0 text-center text-[15px] leading-none">🔗</span>
              <span className="text-[13px] leading-[1.35]">{t("No warning when guests open your links")}</span>
            </div>
            <span className="mt-[2px] text-center text-[12px] italic opacity-75">
              {t("and much more...")}
            </span>
          </div>
        </div>
      </div>
    </ModalFrame>
  );
}
